/**
 * UmsatzsteuerPage — M8 USt-Voranmeldungs-Vorschau (§18 UStG), Soll-Pfad (M8b).
 *
 * Einzige Firebase-Schicht der UStVA-Sicht: lädt owner-scoped (M0) `invoices` +
 * `expenses` des Nutzers (Muster `DatevExportPage`), normalisiert die Rechnungen zu
 * `VatIncomeEntry` (Per-Satz-Reader `reconstructPerRate`, BT-72-Periodisierung) und
 * die Ausgaben via `toVatInputAggregate`, ruft die reine `buildVatReturn`-Aggregation
 * und reicht das Ergebnis an die props-getriebenen Präsentations-Komponenten.
 *
 * KEINE Steuerlogik hier — alles über `buildVatReturn` / `reconstructPerRate` (Golden
 * Rule: kein Copy-Paste, pure functions). Integer-Cents an den Mapper-Rändern.
 *
 * Soll-Pfad (M8b): kein `payments`-Load, `taxationMode` für die Berechnung = 'soll'.
 *
 * AP-1 (Freigabe-Gate, fail-closed): Die Soll-Zahlen sind NUR für Soll-Versteuerer (und
 * §19-Kleinunternehmer, für die die Versteuerungsart irrelevant ist) fachlich korrekt.
 * Ist-Versteuerer (`sellerData.taxationMode === 'ist'`) und Nutzer mit unbekannter/nicht
 * hinterlegter Versteuerungsart bekommen KEINE Soll-Zahlen, sondern einen sauberen
 * Sperr-Zustand („für Ist noch nicht verfügbar" bzw. „Versteuerungsart hinterlegen").
 * Die volle Ist-Berechnung ist M8c (hinter `REACT_APP_USTVA_IST_ENABLED`). Muster:
 * `deriveVatReserveView` (§19 zuerst, dann ist, dann unbekannt, zuletzt soll).
 *
 * Fail-closed: gerendert nur, wenn `REACT_APP_USTVA_ENABLED === 'true'` (Route ebenfalls
 * flag-gated). Lade-/Fehlerzustand statt 0-€-Kennzahlen (kein fail-open, Golden Rule).
 *
 * @module UmsatzsteuerPage
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconVat, IconError, IconInfo } from '../icons';
import { Timestamp } from 'firebase/firestore';

import { useAuth } from '../context/AuthContext';
import { loadInvoicesForUser } from '../utils/datev/datevSource';
import { Expense } from '../types/expenseTypes';
import { InvoiceData } from '../types/invoiceTypes';
import { getExpensesForUser } from '../api/expensesApi';
import { mapInvoiceToVatIncomeEntry } from '../utils/vatIncome';
import {
  SummaryPeriod,
  SummaryPeriodKind,
  resolvePeriod,
  currentQuarterPeriod,
  isWithinPeriod,
} from '../utils/expenseSummary';
import {
  buildVatReturn,
  toVatInputAggregate,
  TaxationMode,
  VatIncomeEntry,
} from '../utils/vatReturn';
import logger from '../utils/logger';
import { detectDownPaymentPresence } from '../utils/ustvaExport';
import VatReturnView, { DrilldownEntry } from '../components/umsatzsteuer/VatReturnView';
import UstvaExportPanel from '../components/umsatzsteuer/UstvaExportPanel';
import PeriodModeSelector from '../components/umsatzsteuer/PeriodModeSelector';
import { canUseUstva, canExportUstvaXml, USTVA_FLAG } from '../config/ustvaAccess';
import { resolveTier } from '../config/entitlements';
import FeatureTeaser from '../components/premium/FeatureTeaser';
import Card from '../components/common/Card';

// ─── Feature flags (fail-closed) ─────────────────────────────────────────────────

// Zugriff (Flag + Beta-Allowlist + Ultra-Tier) lebt jetzt in `config/ustvaAccess.ts`
// (geteilt mit Nav + Route-Registrierung) — `canUseUstva(uid, tier)` unten.
/** Sub-Flag: Ist-Pfad (payments-Load + anteilige Vereinnahmung) — M8c. Default aus. */
const USTVA_IST_ENABLED = process.env.REACT_APP_USTVA_IST_ENABLED === 'true';

// ─── Firestore invoice doc shape (relevant subset) ──────────────────────────────

/** Roh-Form eines `invoices`-Dokuments, soweit für die UStVA-Vorschau relevant. */
interface InvoiceDoc {
  id: string;
  userId: string;
  invoiceNumber?: string;
  invoiceDate?: string | Timestamp;
  buyerName?: string;
  totalAmount?: number;
  currency?: string;
  isCancellation?: boolean;
  isCancelled?: boolean;
  // Optionale Anzahlungs-Marker (§7.5, Rev. 3) — top-level Spiegelung der Writer-Realität
  // (`InvoiceRepository.php:160-164`). Machen `InvoiceDoc` strukturell zuweisbar an
  // `DownPaymentProbeDoc`, damit der geladene Datensatz 1:1 in den Detektor fließt.
  isDownPayment?: boolean;
  type?: string;
  notes?: string | null;
  subject?: string | null;
  /** Komplettes InvoiceData (einzige Quelle für Per-Satz-Split + BT-72). */
  rawData?: Partial<InvoiceData>;
}

// ─── Date helpers ────────────────────────────────────────────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// ─── Income mapper: InvoiceDoc → VatIncomeEntry ──────────────────────────────────
// Die Normalisierung `invoices`-Doc → `VatIncomeEntry` lebt seit M-CFO-0 in
// `utils/vatIncome.ts#mapInvoiceToVatIncomeEntry` (geteilt mit der USt-Rücklage-
// Kachel, Copy-Paste-Verbot). `InvoiceDoc` ist ein Superset des dort erwarteten
// `VatInvoiceDoc` und daher strukturell zuweisbar — kein Verhaltensdelta.

// ─── Invoice loader (WL-K7: shared `loadInvoicesForUser`, owner-scoped M0) ───────
// Die Firestore-Query lebt jetzt in `utils/datev/datevSource` (kein Copy-Paste);
// die UStVA-spezifische Doc-Form `InvoiceDoc` wird als Typparameter gereicht.

// ─── Default-Periode: laufendes Quartal (§5.5) ──────────────────────────────────

function defaultPeriodState(): { kind: SummaryPeriodKind; year: number; quarter: number; month: number } {
  const todayISO = getTodayISO();
  const p = currentQuarterPeriod(todayISO);
  const month = Number.parseInt(todayISO.slice(5, 7), 10);
  return {
    kind: 'quarter',
    year: p.year,
    quarter: p.quarter ?? Math.ceil(month / 3),
    month,
  };
}

// ─── Upsell-Teaser ───────────────────────────────────────────────────────────────

/**
 * Upsell-Teaser für nicht-berechtigte Nutzer bei aktivem `USTVA_FLAG`
 * (Ziel-Tier Ultra). Ersetzt das bisherige `return null` — Muster
 * `BankReconciliationPage` (`layout="page"`: der Teaser IST die gesamte Seite).
 * Rein präsentational, keine Firestore-Reads.
 *
 * Wording-Auflagen (jurist/e-rechnung, frühere Runden): das Feature ist eine
 * **Vorschau/Aufbereitung** der Kennzahlen — KEINE Steuerberatung und KEINE
 * Zusage einer Übermittlung an ELSTER (die Übermittlung nimmt der Nutzer
 * selbst vor; der XML-Export ist zudem ein separat gegatetes Sub-Feature,
 * `USTVA_XML_EXPORT_FLAG`, und darf hier nicht als Kaufargument auftauchen).
 *
 * Jurist-BLOCK B-1 (Re-Loop 1, plan-feedback-jurist-ultra-upsell.md §1): die
 * Beschreibung ist der VERBATIM freigegebene Wortlaut. Zwei tragende Teile
 * dürfen NIE gekürzt oder umformuliert werden:
 *   1. die Soll-/Ist-Einschränkung — nach dem Kauf greift ein ZWEITES Gate
 *      (`gate` unten): nur `isSmallBusiness || taxationMode === 'soll'` sieht
 *      die Vorschau. Ist-Versteuerung ist nach § 20 S. 1 Nr. 1/Nr. 3 UStG in
 *      der Zielgruppe (KMU ≤ 800.000 €, Freiberufler ohne Grenze) der
 *      Regelfall — ohne diesen Satz kauft ein Ist-Versteuerer eine Leistung,
 *      die er nicht erhält (§ 5 Abs. 1, Abs. 2 Nr. 1 UWG).
 *   2. „Die Übermittlung und die steuerliche Beurteilung bleiben bei Ihnen
 *      bzw. Ihrer Steuerkanzlei." — laut Jurist §5.1 der TRAGENDE Freigabegrund
 *      gegenüber § 5 Abs. 1 StBerG. Ohne ihn keine Freigabe.
 * Der Zusatz „(UStVA)"/„deutsche" in den 5 Fremdsprachen ist Teil desselben
 * Wortlauts (Fund M-7: Bezug auf die deutsche Voranmeldung fehlte dort).
 */
const UstvaTeaser: React.FC = () => (
  <FeatureTeaser
    layout="page"
    icon={<IconVat size={30} aria-hidden />}
    badgeKey="ustva.teaserBadge"
    badgeDefault="Ultra-Funktion"
    titleKey="ustva.teaserTitle"
    titleDefault="Umsatzsteuer-Voranmeldung: Vorschau"
    descriptionKey="ustva.teaserDescription"
    descriptionDefault="FaeBill bereitet die Kennzahlen Ihrer Umsatzsteuer-Voranmeldung aus Rechnungen und Ausgaben auf — als Vorschau zum Abgleich. Derzeit nur für die Soll-Versteuerung (Besteuerung nach vereinbarten Entgelten); für die Ist-Versteuerung folgt die Vorschau später. Die Übermittlung und die steuerliche Beurteilung bleiben bei Ihnen bzw. Ihrer Steuerkanzlei."
    highlights={[
      {
        labelKey: 'ustva.teaserHighlight1',
        defaultLabel: 'Kennzahlen je Zeitraum aus Ihren Belegen aufbereitet',
      },
      {
        labelKey: 'ustva.teaserHighlight2',
        defaultLabel: 'Umsatzsteuer und Vorsteuer gegenübergestellt',
      },
      {
        labelKey: 'ustva.teaserHighlight3',
        defaultLabel: 'Jede Zahl bis zur einzelnen Rechnung nachvollziehbar',
      },
    ]}
    ctaKey="ustva.teaserCta"
    ctaDefault="Jetzt auf Ultra upgraden"
    promotionId="feature_teaser_ustva"
    creativeSlot="page"
  />
);

/**
 * Sachlicher Hinweis STATT Kauf-CTA für Nutzer, die die Vorschau auch nach
 * einem Upgrade nicht bekämen (Jurist-BLOCK B-1, Orchestrator-Entscheidung
 * Re-Loop 1): `gate === 'istUnavailable'`, d. h. im Profil steht
 * Ist-Versteuerung, und `REACT_APP_USTVA_IST_ENABLED` ist aus.
 *
 * Bewusste Unterschiede zum {@link UstvaTeaser}:
 *   - KEIN CTA und KEIN Upgrade-Link. Eine Kaufaufforderung an jemanden zu
 *     richten, der die Leistung nachweislich nicht erhält, ist genau der
 *     Schaden, den dieser PR beseitigt (§ 5 Abs. 1, Abs. 2 Nr. 1 UWG).
 *   - KEINE `promotionId`/`view_promotion`-Telemetrie: das hier ist keine
 *     Promotion-Fläche, sondern eine Statusmeldung. Sie als Promotion zu
 *     zählen würde die GA4-Conversion-Rate der UStVA-Fläche verwässern.
 *   - Wortlaut aus dem `ustva`-Namespace (`gate.istTitle`/`gate.istBody`) —
 *     dieselben, bereits in 6 Sprachen gepflegten und juristisch abgenommenen
 *     Strings, die der Sperrbildschirm NACH dem Kauf zeigt. Kein zweiter,
 *     driftender Wortlaut für denselben Sachverhalt.
 *   - `gate.istNoUpgradeHint` ist der EINZIGE neue Satz: er erklärt, warum
 *     hier kein Upgrade-Button steht, und verhindert damit den Fehlkauf über
 *     einen anderen Einstieg (z. B. die Sidebar-Karte).
 *
 * Der Fall `gate === 'unconfigured'` bekommt bewusst WEITERHIN den Teaser mit
 * CTA: dort ist die Versteuerungsart schlicht nicht hinterlegt, der Ausgang
 * also offen — und der (verbatim freigegebene) Einschränkungssatz in der
 * Teaser-Beschreibung trägt die nötige Information vor dem Kauf.
 */
const UstvaIstUnavailableNotice: React.FC = () => {
  const { t } = useTranslation('ustva');
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <Card className="max-w-lg w-full text-center">
        <div className="flex justify-center mb-5">
          <div className="bg-[var(--tone-neutral-bg)] rounded-full p-4 text-[var(--tone-neutral-fg)] text-3xl">
            <IconVat size={30} aria-hidden />
          </div>
        </div>

        {/* Bewusst OHNE Eyebrow-Badge: der Badge des Teasers ist ein
            Verkaufs-Signal („Ultra-Funktion"). Hier wird nichts verkauft, und
            eine Badge-Zeile, die nur die Überschrift doppelt, trägt keine
            eigene Information. */}
        <h1 className="text-xl font-bold text-primary mb-3">
          {t('gate.istTitle', 'Vorschau für die Ist-Versteuerung noch nicht verfügbar')}
        </h1>

        <p className="text-secondary mb-3 leading-relaxed">
          {t(
            'gate.istBody',
            'Die USt-Voranmeldungs-Vorschau ist derzeit nur für die Soll-Versteuerung (nach vereinbarten Entgelten) verfügbar. Für die Ist-Versteuerung (nach vereinnahmten Entgelten) ist sie noch nicht verfügbar.',
          )}
        </p>

        <p className="text-sm text-muted leading-relaxed">
          {t(
            'gate.istNoUpgradeHint',
            'Ein Upgrade schaltet die Vorschau für die Ist-Versteuerung nicht frei — sobald sie verfügbar ist, finden Sie sie hier.',
          )}
        </p>
      </Card>
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────────

/**
 * UStVA-Vorschau-Seite (Soll-Pfad). Lädt Belege owner-scoped, aggregiert pro Periode
 * und rendert die Kennzahlen-Sicht inkl. Disclaimer/Scope-Liste. Fail-closed ohne Flag:
 * bei aktivem `USTVA_FLAG`, fehlender Berechtigung UND eigenem Scope tritt der
 * {@link UstvaTeaser} an die Stelle des früheren `return null`
 * (Ultra-Upsell-Konsistenz 2026-07-29); bei Flag AUS oder im FREMDEN Scope
 * bleibt es beim `return null` (R-1: `<AdvisorHidden>` fängt nur `advisor` ab,
 * nicht `member` auf einem fremden Tenant — s. Access-Gate unten).
 */
const UmsatzsteuerPage: React.FC = () => {
  const { t } = useTranslation('ustva');
  const { currentUser, sellerData, effectiveScopeUid } = useAuth();
  // R-1 (Re-Loop 3): „betrachte ich meinen EIGENEN Tenant?" — Muster
  // `DatevExportPage.tsx:116` / `TravelExpensesPage` (F-1). Steuert
  // ausschließlich den Nicht-Berechtigt-Zweig (Teaser bzw. Ist-Hinweis, s.
  // Access-Gate unten); der Inhalt für Berechtigte bleibt unberührt.
  const isOwnScope = !!currentUser && effectiveScopeUid === currentUser.uid;

  const [income, setIncome] = useState<VatIncomeEntry[]>([]);
  // Roh-Docs bleiben erhalten für die reine Anzahlungs-Detektion (§7.5) — derselbe
  // Datensatz, der auch `income`/`buildVatReturn` speist (kein zweiter Load).
  const [invoiceDocs, setInvoiceDocs] = useState<InvoiceDoc[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Perioden-State (§5.5).
  const [periodState, setPeriodState] = useState(defaultPeriodState);

  const isSmallBusiness = sellerData?.isSmallBusiness === true;
  const rawTaxationMode = sellerData?.taxationMode;
  // Read-only-Anzeige der Versteuerungsart im PeriodModeSelector (nur Preview-Pfad).
  const configuredMode: TaxationMode = rawTaxationMode === 'ist' ? 'ist' : 'soll';
  // M8b berechnet AUSSCHLIESSLICH auf Soll-Basis: der Ist-Pfad (payments-Load + anteilige
  // Vereinnahmung) ist M8c. Würde hier 'ist' an buildVatReturn gehen, ohne dass payments
  // geladen sind, fiele jede Einnahme aus der Periode (kein paidInPeriodGrossCents) — daher
  // bewusst hart 'soll'.
  const computeMode: TaxationMode = 'soll';

  // ─── AP-1: Fail-closed Versteuerungsart-Gate ─────────────────────────────────────
  // Die (Soll-)Vorschau ist fachlich nur für Soll-Versteuerer korrekt; §19-Kleinunternehmer
  // haben ohnehin keine eigene USt (Sonderfall in VatReturnView), für sie ist die
  // Versteuerungsart irrelevant. Ist-Versteuerer bekommen sonst systematisch zu hohe Zahlen,
  // Nutzer ohne hinterlegte Versteuerungsart sind unbekannt ⇒ fail-closed aussperren
  // (Sicherheit vor Bequemlichkeit: lieber einen Soll-Versteuerer zu viel aussperren als
  // einem Ist-Versteuerer falsche Zahlen zeigen). Reihenfolge wie `deriveVatReserveView`.
  const gate: 'preview' | 'istUnavailable' | 'unconfigured' =
    isSmallBusiness || rawTaxationMode === 'soll'
      ? 'preview'
      : rawTaxationMode === 'ist'
        ? 'istUnavailable'
        : 'unconfigured';

  // Aufgelöster Zeitraum aus dem Perioden-State.
  const period: SummaryPeriod = useMemo(() => {
    const { kind, year, quarter, month } = periodState;
    const refMonth = kind === 'quarter' ? (quarter - 1) * 3 + 1 : kind === 'month' ? month : 1;
    const refISO = `${year}-${pad2(refMonth)}-01`;
    try {
      return resolvePeriod(kind, refISO);
    } catch (err) {
      logger.error('UmsatzsteuerPage: resolvePeriod failed, fallback to current quarter', err);
      return currentQuarterPeriod(getTodayISO());
    }
  }, [periodState]);

  useEffect(() => {
    // Nur der Preview-Pfad (Soll / §19-KU) lädt Belege; die Sperr-Zustände zeigen keine
    // Zahlen und ersparen sich den owner-scoped Read (Muster VatReserveTile.needsIncomeLoad).
    if (!canUseUstva(currentUser?.uid, resolveTier(sellerData)) || !currentUser || gate !== 'preview') {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        // Fail-closed: beide Quellen müssen laden, sonst Fehlerzustand (kein Teil-Ergebnis,
        // 0-€-Kennzahlen würden „nichts zu zahlen" suggerieren = fail-open).
        const [rawInvoices, rawExpenses] = await Promise.all([
          loadInvoicesForUser<InvoiceDoc>(effectiveScopeUid),
          getExpensesForUser(effectiveScopeUid),
        ]);
        if (cancelled) return;
        setInvoiceDocs(rawInvoices);
        setIncome(rawInvoices.map(mapInvoiceToVatIncomeEntry));
        setExpenses(rawExpenses);
      } catch (err) {
        if (!cancelled) {
          logger.error('UmsatzsteuerPage: load failed', err);
          setError(String(err instanceof Error ? err.message : err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser, effectiveScopeUid, sellerData, gate]);

  // Reine Aggregation (§3.1). Eingangs-Adapter + buildVatReturn rein, useMemo-cached.
  const figures = useMemo(() => {
    const input = toVatInputAggregate(expenses, period);
    return buildVatReturn(income, input, period, computeMode, isSmallBusiness);
  }, [income, expenses, period, computeMode, isSmallBusiness]);

  // Anzahl der in der Periode erfassten Belege mit unsicherem Fallback-Datum (§5.5-Hinweis).
  const fallbackDateCount = useMemo(() => {
    if (isSmallBusiness) return 0;
    return income.reduce((count, e) => {
      if (e.isCancellation === true) return count;
      if (!e.periodDateIsFallback) return count;
      const periodDate = e.serviceDateISO ?? e.invoiceDateISO;
      return isWithinPeriod(periodDate, period) ? count + 1 : count;
    }, 0);
  }, [income, period, isSmallBusiness]);

  // G-3 (§8.1 INFO): perioden-gefilterte, geladene Ausgaben — reiner Anzeige-Input für die
  // Nullmeldungs-Differenzierung im Export-Panel („0 Belege geladen"). KEIN Gate-Input.
  const periodExpenseCount = useMemo(
    () => expenses.filter((e) => isWithinPeriod(e.expenseDate, period)).length,
    [expenses, period],
  );

  // B9 (§7.5, Rev. 3): reine In-Memory-Anzahlungs-Detektion über DENSELBEN geladenen
  // Datensatz (kein Firestore-Call, kein Fehlpfad — bewusst NICHT perioden-gefiltert).
  const downPaymentInfo = useMemo(
    () => detectDownPaymentPresence(invoiceDocs),
    [invoiceDocs],
  );

  // Belegbezogener Drilldown (GoBD-Geist, §5.7): in der Periode erfasste Einnahmen.
  const drilldown = useMemo<DrilldownEntry[]>(() => {
    if (isSmallBusiness) return [];
    return income
      .filter((e) => {
        if (e.isCancellation === true) return false;
        const periodDate = e.serviceDateISO ?? e.invoiceDateISO;
        return isWithinPeriod(periodDate, period);
      })
      .map((e) => ({
        id: e.id,
        dateISO: e.serviceDateISO ?? e.invoiceDateISO,
        grossCents: e.grossCents,
        periodDateIsFallback: e.periodDateIsFallback,
      }));
  }, [income, period, isSmallBusiness]);

  // Fail-closed: ohne Zugriff (Beta-UID ODER Flag+Ultra) keine Inhalte. Bei
  // aktivem Flag stattdessen der Upsell-Teaser (Ultra-Upsell-Konsistenz
  // 2026-07-29), bei Flag AUS byte-identisch zum bisherigen `return null`.
  if (!canUseUstva(currentUser?.uid, resolveTier(sellerData))) {
    // R-1 (Reviewer-Empfehlung, Re-Loop 3): `isOwnScope` zusätzlich zum Flag —
    // dieselbe Fehlerklasse wie F-1 bei `TravelExpensesPage`, nur über eine
    // andere Rolle. `<AdvisorHidden>` (das diese Route wrappt) gated über
    // `useIsReadOnly()`, also über `activeRole === 'advisor'`. `TenantRole` kennt
    // aber drei Werte (`'owner' | 'member' | 'advisor'`): ein **member** auf
    // einem FREMDEN Tenant hat `activeRole === 'member'`, fällt damit durch den
    // rollenbasierten Guard und stünde hier vor einem Ultra-Kauf-CTA, der sich
    // auf den Tarif-Zustand eines fremden Tenants bezieht. `tier`/`sellerData`
    // sind die des eingeloggten Nutzers, nur `effectiveScopeUid` wechselt.
    // Fail-closed wie bei F-1: fremder Scope ⇒ `return null`, weder Teaser noch
    // Ist-Hinweis (auch letzterer spricht über die Versteuerungsart des
    // EIGENEN Profils und wäre im fremden Scope irreführend).
    if (USTVA_FLAG && isOwnScope) {
      // Jurist-BLOCK B-1: steht schon VOR dem Kauf fest, dass dieser Nutzer die
      // Vorschau auch danach nicht bekäme (Ist-Versteuerung im Profil, Ist-Pfad
      // nicht ausgeliefert), dann KEIN Kauf-CTA — nur der sachliche Hinweis.
      // `gate === 'unconfigured'` behält den Teaser: dort ist der Ausgang offen,
      // und der Einschränkungssatz in der Beschreibung trägt die Information.
      if (gate === 'istUnavailable') {
        return <UstvaIstUnavailableNotice />;
      }
      return <UstvaTeaser />;
    }
    return null;
  }

  return (
    <div className="min-h-screen bg-surface p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
            <IconVat className="text-accent-ink" size={24} />
            {t('title')}
          </h1>
          <p className="text-muted text-sm mt-1">{t('subtitle')}</p>
        </div>

        {/* AP-1 Sperr-Zustand: Ist-Versteuerer / unbekannte Versteuerungsart bekommen
            KEINE (fachlich falschen) Soll-Zahlen, sondern einen sauberen Hinweis. */}
        {gate !== 'preview' && (
          <div className="mt-6 rounded-lg border border-[var(--tone-accent-border)] bg-[var(--tone-accent-bg)] p-5">
            <div className="flex items-start gap-3">
              <span className="text-accent-ink mt-0.5">
                <IconInfo size={18} />
              </span>
              <div className="text-sm text-[var(--tone-accent-fg)]">
                <p className="font-semibold">
                  {t(gate === 'istUnavailable' ? 'gate.istTitle' : 'gate.unconfiguredTitle')}
                </p>
                <p className="mt-1">
                  {t(gate === 'istUnavailable' ? 'gate.istBody' : 'gate.unconfiguredBody')}
                </p>
                <Link
                  to="/dashboard/konto"
                  className="mt-3 inline-block text-accent-ink underline"
                >
                  {t('gate.toProfile')}
                </Link>
              </div>
            </div>
          </div>
        )}

        {gate === 'preview' && (
          <>
            <PeriodModeSelector
              kind={periodState.kind}
              year={periodState.year}
              quarter={periodState.quarter}
              month={periodState.month}
              taxationMode={configuredMode}
              istEnabled={USTVA_IST_ENABLED}
              onKindChange={(kind) => setPeriodState((s) => ({ ...s, kind }))}
              onYearChange={(year) => setPeriodState((s) => ({ ...s, year }))}
              onQuarterChange={(quarter) => setPeriodState((s) => ({ ...s, quarter }))}
              onMonthChange={(month) => setPeriodState((s) => ({ ...s, month }))}
            />

            {loading && (
              <div className="mt-6 text-muted text-sm">{t('loading')}</div>
            )}

            {!loading && error && (
              <div className="mt-6 p-4 rounded-lg border border-[var(--tone-danger-border)] bg-[var(--tone-danger-bg)]">
                <div className="flex items-center gap-2 text-[var(--tone-danger-fg)] font-medium">
                  <IconError />
                  {t('error.title')}
                </div>
                <button
                  type="button"
                  onClick={() => setPeriodState((s) => ({ ...s }))}
                  className="mt-3 px-3 py-1.5 text-sm rounded bg-red-700 hover:bg-red-600 text-white"
                >
                  {t('error.retry')}
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                <VatReturnView
                  figures={figures}
                  fallbackDateCount={fallbackDateCount}
                  drilldown={drilldown}
                />
                {/* Export-Panel als Geschwister UNTER der Vorschau — nur bei separatem
                    Export-Zugriff (Beta-UID ODER Sub-Flag). VatReturnView bleibt unberührt. */}
                {sellerData && canExportUstvaXml(currentUser?.uid, resolveTier(sellerData)) && (
                  <UstvaExportPanel
                    figures={figures}
                    seller={sellerData}
                    period={period}
                    fallbackDateCount={fallbackDateCount}
                    periodExpenseCount={periodExpenseCount}
                    downPaymentInfo={downPaymentInfo}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UmsatzsteuerPage;
